import { cn } from '../lib/utils';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../components/ui/Button';
import ExportLogsModal from '../components/ExportLogsModal';
import Filters from "../components/features/submissions/Filters";
import Toolbar from "../components/features/submissions/Toolbar";
import SubmissionsTable from "../components/features/submissions/SubmissionsTable";
import ReassignModal from "../components/features/submissions/ReassignModal";
import DataTablePagination from '../components/common/DataTablePagination';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useMicrosoftAuth } from "../context/MicrosoftAuthContext";
import 'react-loading-skeleton/dist/skeleton.css'
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getMailroomConfigs } from '../api/suggestionService';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { formatDateAU as formatDate, formatRemainingTime } from '../utils/formatters';
import { cancelSubmission } from '../api/submissionService';
import { downloadBlobFile } from '../utils/fileHelper';

const Submission = ({ isOpen, toggle }) => {
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [reassignModalOpen, setReassignModalOpen] = useState(false);
    const [reassignTarget, setReassignTarget] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { userTeamData } = useMicrosoftAuth();
    const { isDark } = useTheme();
    const [submissions, setSubmissions] = useState([]);
    const [loadingApi, setLoadingApi] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        completed: 0,
        cancelled: 0,
        issues: 0,
        error: 0
    });

    const getInitialPagination = (location) => {
        const query = new URLSearchParams(location.search);
        const clearData = query.get('cleardata');
        const stored = localStorage.getItem('submission_pagination');
        if (clearData !== null) {
            navigate('/submission', { replace: true });
        }
        if (clearData == 'false' && stored) {
            try {
                const { page, limit, searchQuery, planSearchQuery, status, startDate, endDate } = JSON.parse(stored);
                const { viewMode } = JSON.parse(stored);
                return {
                    page: Number(page) || 1,
                    limit: Number(limit) || 10,
                    searchQuery: searchQuery || '',
                    planSearchQuery: planSearchQuery || '',
                    startDate: startDate ? new Date(startDate) : '',
                    endDate: endDate ? new Date(endDate) : '',
                    status: status || '',
                    viewMode: viewMode || 'my',
                };
            } catch {
                return { page: 1, limit: stored ? JSON.parse(stored).limit : 10, searchQuery: '', planSearchQuery: '', startDate: '', endDate: '', status: '', viewMode: 'my' };
            }
        }
        return { page: 1, limit: stored ? JSON.parse(stored).limit : 10, searchQuery: '', planSearchQuery: '', startDate: '', endDate: '', status: '', viewMode: 'my' };
    };

    const initialPagination = getInitialPagination(location);
    const [limit, setLimit] = useState(initialPagination.limit);        // 10, 20, 50, 100
    const [page, setPage] = useState(initialPagination.page);           // current page
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState(initialPagination.searchQuery);
    const [planSearchQuery, setPlanSearchQuery] = useState(initialPagination.planSearchQuery || '');
    const searchInputRef = useRef(null);
    const shouldFocusSearch = useRef(false);
    const [dateRange, setDateRange] = useState({ startDate: initialPagination.startDate, endDate: initialPagination.endDate });
    const [statusFilter, setStatusFilter] = useState(initialPagination.status);
    const [groupedCategories, setGroupedCategories] = useState([]);
    const [selectedGroupingCategory, setSelectedGroupingCategory] = useState('all');
    const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
    const [viewMode, setViewMode] = useState(initialPagination.viewMode || 'my'); // 'my' | 'all'

    useEffect(() => {
        localStorage.setItem('submission_pagination', JSON.stringify({ page, limit, searchQuery, planSearchQuery, status: statusFilter, startDate: dateRange.startDate, endDate: dateRange.endDate, viewMode }));
    }, [page, limit, searchQuery, planSearchQuery, statusFilter, dateRange, viewMode]);

    useEffect(() => {
        const fetchMailroomConfigs = async () => {
            setIsLoadingConfigs(true);
            try {
                const configs = await getMailroomConfigs();

                const grouped = configs
                    .filter(config => !config.send_category.includes('***'))
                    .reduce((groups, config) => {
                        const groupKey = config.grouping_category_id || 'ungrouped';
                        const groupName = config.grouping_category_name || 'Ungrouped';

                        if (!groups[groupKey]) {
                            groups[groupKey] = {
                                id: groupKey,
                                name: groupName,
                                position: config.grouping_category_position,
                                configIds: []
                            };
                        }
                        groups[groupKey].configIds.push(config.id);
                        return groups;
                    }, {});

                // Convert to array and sort by position
                const sortedGroups = Object.values(grouped)
                    .sort((a, b) => {
                        if (a.position == null && b.position == null) return 0;
                        if (a.position == null) return 1;
                        if (b.position == null) return -1;
                        return a.position - b.position;
                    });

                setGroupedCategories(sortedGroups);
            } catch (error) {
                console.error('Error fetching mailroom configs:', error);
            } finally {
                setIsLoadingConfigs(false);
            }
        };

        fetchMailroomConfigs();
    }, []);

    useEffect(() => {
        fetchSubmissions();
    }, [limit, page, dateRange, statusFilter, searchQuery, planSearchQuery, selectedGroupingCategory, viewMode]);

    const fetchSubmissions = async () => {
        try {
            setLoadingApi(true);
            const data = new FormData();
            data.append('page', page);
            data.append('limit', limit);
            data.append('status', statusFilter.toLowerCase());
            data.append('startDate', dateRange.startDate || '');
            data.append('endDate', dateRange.endDate || '');
            data.append('user_team_id', userTeamData?.id);
            data.append('user_role_id', userTeamData?.user_role_id);
            data.append('user_email', userTeamData?.email);
            data.append('view_mode', viewMode);
            if (searchQuery) {
                data.append('search', searchQuery);
            }
            if (planSearchQuery) {
                data.append('planSearch', planSearchQuery);
            }

            userTeamData?.user_group?.forEach(group => {
                data.append('user_group[]', group);
            });

            userTeamData?.sm_managed_plans?.forEach(plan => {
                data.append('sm_plan_numbers[]', plan);
            });

            if (userTeamData?.branch_manager_branches && userTeamData.branch_manager_branches.length > 0) {
                userTeamData.branch_manager_branches.forEach(branch => {
                    data.append('branch_manager_branches[]', branch.branch_id);
                });
            }

            if (selectedGroupingCategory && selectedGroupingCategory !== 'all') {
                const selectedCategory = groupedCategories.find(cat => cat.id === selectedGroupingCategory || cat.id === parseInt(selectedGroupingCategory));
                if (selectedCategory && selectedCategory.configIds) {
                    selectedCategory.configIds.forEach(configId => {
                        data.append('smata_config_ids[]', configId);
                    });
                }
            }

            const response = await api.post('/getUserSubmissions', data);

            const total = response.data.total;
            setTotalPages(Math.ceil(total / limit));
            const submissionsData = response.data.data;
            setSubmissions(submissionsData);

            // Initialize countdown timers for each submission
            const initialTimeLeft = {};
            submissionsData.forEach(submission => {
                // const remainingMinutes = getRemainingMinutes(submission.createdAt);
                // if (remainingMinutes > 0) {
                // initialTimeLeft[submission.id] = remainingMinutes;
                initialTimeLeft[submission.id] = submission.createdAt;
                // }
            });

            // Calculate stats
            setStats({
                total: submissionsData.length,
                completed: submissionsData.filter(s => s.status === 'complete').length,
                cancelled: submissionsData.filter(s => s.status === 'cancelled').length,
                issues: submissionsData.filter(s => s.status === 'issues').length,
                error: submissionsData.filter(s => s.status === 'error').length
            });

            setLoadingApi(false);

            if (shouldFocusSearch.current) {
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                    shouldFocusSearch.current = false; // Reset the flag
                }, 100);
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
        } finally {
            setLoadingApi(false);
        }
    };

    const handleCancelSubmission = async (submissionId) => {
        try {
            await cancelSubmission(submissionId, userTeamData);

            // Refresh list upon success
            fetchSubmissions();
        } catch (error) {
            console.error('Cancellation failed:', error.message);
        }
    };

    // Handle status filter change when filter buttons are clicked
    const handleStatusFilterChange = (status) => {
        if (status === 'My') {
            setViewMode('my');
            setStatusFilter('All');
        } else if (status === 'Related') {
            setViewMode('related');
            setStatusFilter('All');
        } else if (status === 'All' || !status || status === '') {
            setViewMode('all');
            setStatusFilter('All');
        } else {
            setStatusFilter(status);
        }
        setPage(1);
    };

    // Handle date range filter change
    const handleDateRangeChange = (startDate, endDate) => {
        // If both dates are null, clear the filter

        if (!startDate && !endDate) {
            setDateRange({ startDate: null, endDate: null });
            return;
        }

        // If we have valid dates, apply the filter
        if (startDate && endDate) {
            const startDateFormatted = dayjs(startDate).format('YYYY-MM-DD 00:00:00');
            const endDateFormatted = dayjs(endDate).format('YYYY-MM-DD 23:59:59');

            setPage(1);
            setDateRange({ startDate: startDateFormatted, endDate: endDateFormatted });
        }
    };

    const handleGroupingCategoryChange = (categoryId) => {
        setSelectedGroupingCategory(categoryId);
        setPage(1);
    };

    const handleExportFilteredSubmissions = async () => {
        try {
            const data = new FormData();
            data.append('status', statusFilter.toLowerCase());
            data.append('startDate', dateRange.startDate || '');
            data.append('endDate', dateRange.endDate || '');
            data.append('user_team_id', userTeamData?.id);
            data.append('user_role_id', userTeamData?.user_role_id);
            data.append('user_email', userTeamData?.email);

            if (searchQuery) {
                data.append('search', searchQuery);
            }

            if (planSearchQuery) {
                data.append('planSearch', planSearchQuery);
            }

            userTeamData?.user_group?.forEach(group => {
                data.append('user_group[]', group);
            });

            userTeamData?.sm_managed_plans?.forEach(plan => {
                data.append('sm_plan_numbers[]', plan);
            });

            if (userTeamData?.branch_manager_branches && userTeamData.branch_manager_branches.length > 0) {
                userTeamData.branch_manager_branches.forEach(branch => {
                    data.append('branch_manager_branches[]', branch.branch_id);
                });
            }

            if (selectedGroupingCategory && selectedGroupingCategory !== 'all') {
                const selectedCategory = groupedCategories.find(cat => cat.id === selectedGroupingCategory || cat.id === parseInt(selectedGroupingCategory));
                if (selectedCategory && selectedCategory.configIds) {
                    selectedCategory.configIds.forEach(configId => {
                        data.append('smata_config_ids[]', configId);
                    });
                }
            }

            const response = await api.post('/exportFilteredSubmissions', data, {
                responseType: 'blob'
            });

            downloadBlobFile(response.data, `filtered_submissions_${new Date().toISOString().split('T')[0]}.csv`);
        } catch (error) {
            console.error('Error exporting filtered submissions:', error);
        }
    };

    const datae = [
        { status: "My", totalBranches: "", AnalyticsIcon: "", details: "My Submissions", textColor: "text-CD6D2C", buttonLabel: "My Submissions" },
        { status: "Related", totalBranches: "", AnalyticsIcon: "", details: "Related Submissions", textColor: "text-[#32047c]", buttonLabel: "Related Submissions" },
        { status: "All", totalBranches: stats.total.toString(), AnalyticsIcon: "bluecut", details: "Total Submissions", textColor: "text-black", buttonLabel: "All Submissions" },
        { status: "Complete", totalBranches: stats.completed.toString(), AnalyticsIcon: "success", details: "Successful Submissions", textColor: "text-117302", buttonLabel: "Completed" },
        { status: "Pending", totalBranches: stats.pending?.toString() || "0", AnalyticsIcon: "loading", details: "Pending Submissions", textColor: "text-9a7b00", buttonLabel: "Pending" },
        { status: "Cancelled", totalBranches: stats.cancelled.toString(), AnalyticsIcon: "dangur", details: "Cancelled Submissions", textColor: "text-FF0000", buttonLabel: "Cancelled" },
        { status: "Issues", totalBranches: stats.issues.toString(), AnalyticsIcon: "warning", details: "Submissions with Issues", textColor: "text-FF7A00", buttonLabel: "Issues" },
        { status: "Error", totalBranches: stats.error.toString(), AnalyticsIcon: "error", details: "Errored Submissions", textColor: "text-FF0000", buttonLabel: "Error" },
        { status: "Scheduled", totalBranches: stats.scheduled?.toString() || "0", AnalyticsIcon: "clock", details: "Scheduled Submissions", textColor: "text-0D9488", buttonLabel: "Scheduled" },
    ];

    const getFilterButtonClasses = (status, isActive) => {
        const statusLower = status.toLowerCase();
        const state = isActive ? 'selected fw-600' : ' fw-500';
        
        const baseClasses = 'filter-btn';
        const stateClass = `filter-btn-${state}`;
        
        // Color variant class based on status
        let colorClass;
        switch (statusLower) {
            case 'all':
                colorClass = `filter-btn-all-${state}`;
                break;
            case 'complete':
                colorClass = `filter-btn-completed-${state}`;
                break;
            case 'cancelled':
                colorClass = `filter-btn-cancelled-${state}`;
                break;
            case 'issues':
                colorClass = `filter-btn-issues-${state}`;
                break;
            case 'error':
                colorClass = `filter-btn-error-${state}`;
                break;
            case 'scheduled':
                colorClass = `filter-btn-scheduled-${state}`;
                break;
            default:
                colorClass = `filter-btn-all-${state}`;
        }
        
        return `${baseClasses} ${stateClass} ${colorClass}`;
    };

    // Function to get the correct icon path for filter buttons
    const getFilterButtonIcon = (status, isActive) => {
        const statusLower = status.toLowerCase();
        const state = isActive ? 'selected' : 'normal';
        
        switch (statusLower) {
            case 'complete':
                return `/images/filter-icons/completed-${state}.svg`;
            case 'cancelled':
                return `/images/filter-icons/cancelled-${state}.svg`;
            case 'issues':
                return `/images/filter-icons/issues-${state}.svg`;
            case 'error':
                return `/images/filter-icons/error-${state}.svg`;
            case 'scheduled':
                return `/images/filter-icons/scheduled-${state}.svg`;
            case 'pending':
                return `/images/filter-icons/pending-${state}.svg`;
            case 'all':
            default:
                return null;
        }
    };



    const getRemainingMinutes = (createdAt, planNumber, sendLater = false, scheduledTime = null) => {
        const now = new Date();

        if (sendLater && scheduledTime) {
            const scheduledDate = new Date(scheduledTime);
            const diffMinutes = (scheduledDate - now) / (1000 * 60);
            return Math.max(0, diffMinutes);
        }

        const submissionDate = new Date(createdAt);
        const waitTime = planNumber === 'Test' ? 5 : 15;
        const diffMinutes = waitTime - ((now - submissionDate) / (1000 * 60));
        return Math.max(0, diffMinutes);
    };



    const tableData = {
        headers: [
            "Submission Date",
            "Status",
            "Plan No.",
            "Sending Category",
            "Sending From",
            "Document Name",
            "Submitted By",
            "Scheduled At",
            "Reminder Status",
            "Actions",
        ],
        data: submissions.map(submission => {
            const remainingTime = getRemainingMinutes(
                submission.createdAt,
                submission.planNumber,
                submission.send_later,
                submission.scheduled_time
            );
            const canCancel = (submission.status === 'pending' || submission.status === 'scheduled') && remainingTime > 0;
            // Map to Figma-styled chips for Reminder Status column
            let reminderStatus = "";
            let reminderChipClasses = ""; // Tailwind classes: bg + text + size
            if (submission.reminderStatus == 2) {
                // Sent
                reminderStatus = "Sent";
                reminderChipClasses = "bg-[#d9fbe6] text-[#0a6e29]";
            } else if (submission.reminderStatus == 1) {
                // Queued
                reminderStatus = "Queued";
                reminderChipClasses = "bg-[#fff9db] text-[#9a7b00]";
            } else if (submission.reminderStatus == 3) {
                // Cancelled
                reminderStatus = "Cancelled";
                reminderChipClasses = "bg-[#ffe3e3] text-[#b42318]";
            } else if (submission.reminderStatus == 4) {
                // Error (if backend uses 4)
                reminderStatus = "Error";
                reminderChipClasses = "bg-[#ffe0cc] text-orange-700";
            } else if (submission.reminderStatus == 5) {
                reminderStatus = "Issues";
                reminderChipClasses = "bg-[#ffe0cc] text-orange-700";
            } else {
                // None
                reminderStatus = "None";
                reminderChipClasses = "bg-[#f9e3ff] text-purple-700";
            }
            return {
                id: submission.id,
                "Submission Date": formatDate(submission.createdAt),
                "Status": (!remainingTime > 0 && (submission.status == 'pending' || submission.status === 'scheduled')) ? "Processing" : submission.status,
                reassigned_at: submission.reassigned_at || null,
                "Plan No.": submission.planNumber,
                "Sending Category": submission.sending,
                "Submitted By": submission.submitted_by,
                "Sending From": submission.email,
                "Document Name": submission.custom_document_name,
                "Scheduled At": submission.send_later && submission.scheduled_time
                    ? formatDate(submission.scheduled_time)
                    : <span className={cn("text-muted")}>-</span>,
                "Reminder": (
                    <div className={cn("flex items-center gap-[8px]")}>
                        <span className={cn(
                            "inline-flex items-center justify-center rounded-[78px] px-[16px] py-[3px] text-[13px] font-semibold w-[120px]",
                            reminderChipClasses
                        )}>
                            {reminderStatus}
                        </span>
                        {submission.sendReminders === 'yes' && submission.reminderStatus !== '0' && submission.status !== 'pending' && (() => {
                            if (viewMode !== 'all') return true;
                            const isSuperAdmin = userTeamData?.user_role_id?.includes("11");
                            const isAdmin = userTeamData?.user_role_id?.includes("10");
                            if (isSuperAdmin) return true;
                            if (isAdmin) return submission.user_team_id == userTeamData?.id;
                            return submission.user_team_id == userTeamData?.id || submission.email === userTeamData?.email;
                        })() ? (
                            <Button
                                variant="ghost"
                                onClick={() => navigate(`/manage-reminders/${submission.id}`)}
                                className={cn("bg-[#d9fbe6] text-[#CD6D2C] px-[10px] py-[6px] rounded-[10px] hover:bg-[#c4f5d4]")}
                            >
                                <img src="/assets/icons/reminder.svg" alt="Manage Reminders" />
                                <span className={cn("text-[16px] font-medium")}>Manage</span>
                            </Button>
                        ) : null}
                    </div>
                ),
                "Actions":
                    <div className={cn("flex items-center gap-[8px]")}>
                        {/* Cancel Button (conditional) */}
                        {canCancel && (() => {
                            const isSuperAdmin = userTeamData?.user_role_id?.includes("11");
                            const isAdmin = userTeamData?.user_role_id?.includes("10");
                            const userOwnsSubmission = submission.user_team_id == userTeamData?.id || submission.email === userTeamData?.email;
                            if (viewMode === 'all') {
                                if (isSuperAdmin) return true;
                                if (isAdmin) return submission.user_team_id == userTeamData?.id;
                                return userOwnsSubmission;
                            }
                            // My Submissions view
                            return isAdmin ? submission.user_team_id == userTeamData?.id : true;
                        })() ? (
                            <Button
                                variant="ghost"
                                onClick={() => handleCancelSubmission(submission.id)}
                                className={cn("bg-[#f9d6d5] text-red-700 px-[10px] py-[6px] rounded-[10px] hover:bg-[#f6baba]")}
                            >
                                <img src="/assets/icons/cancelsubmission.svg" alt="" className={cn("w-[16px] h-[16px]")} />
                                <span className={cn("text-[16px] font-medium")}>Cancel {`${formatRemainingTime(remainingTime)}`}</span>
                            </Button>
                        ) : null}

                        {/* Reassign Button - only for pending or scheduled */}
                        {(submission.status === 'pending' || submission.status === 'scheduled') && (() => {
                            const isSuperAdmin = userTeamData?.user_role_id?.includes("11");
                            const isAdmin = userTeamData?.user_role_id?.includes("10");
                            const userOwnsSubmission = submission.user_team_id == userTeamData?.id || submission.email === userTeamData?.email;
                            if (viewMode === 'all') {
                                if (isSuperAdmin) return true;
                                if (isAdmin) return true;
                                return false;
                            }
                            return isAdmin ? submission.user_team_id == userTeamData?.id : true;
                        })() ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setReassignTarget(submission);
                                    setReassignModalOpen(true);
                                }}
                                className={cn(
                                    "group px-[10px] py-[6px] rounded-[10px] flex items-center gap-[6px] border-none transition-colors duration-200",
                                    isDark
                                        ? 'bg-[#ffe1cc] text-[#cd6d2c] hover:!bg-[#cd6d2c] hover:!text-[#161515]'
                                        : 'bg-[#ffe1cc] text-[#cd6d2c] hover:!bg-[#cd6d2c] hover:!text-white'
                                )}
                            >
                                <div
                                    className={cn(
                                        "w-[16px] h-[16px] transition-all duration-200",
                                        isDark ? 'bg-[#cd6d2c] group-hover:!bg-[#000000]' : 'bg-[#cd6d2c] group-hover:!bg-white'
                                    )}
                                    style={{
                                        WebkitMaskImage: "url('/assets/icons/reassign.svg')",
                                        WebkitMaskRepeat: 'no-repeat',
                                        WebkitMaskSize: 'contain',
                                        WebkitMaskPosition: 'center',
                                        maskImage: "url('/assets/icons/reassign.svg')",
                                        maskRepeat: 'no-repeat',
                                        maskSize: 'contain',
                                        maskPosition: 'center',
                                    }}
                                    aria-hidden="true"
                                />
                                <span className={cn("text-[16px] font-semibold")}>Reassign</span>
                            </button>
                        ) : null}

                        {/* Logs Button - complete/issues for all visible rows; error for admins only */}
                        {(() => {
                            const isSuperAdmin = userTeamData?.user_role_id?.includes("11");
                            const isAdmin = userTeamData?.user_role_id?.includes("10");
                            const canViewErrorLogs = isSuperAdmin || isAdmin;
                            const canShowLogsButton =
                                submission.status === 'complete' ||
                                submission.status === 'issues' ||
                                (submission.status === 'error' && canViewErrorLogs);

                            return canShowLogsButton;
                        })() && (
                                <button
                                    type="button"
                                    onClick={() => navigate(`/submission-logs/${submission.id}`)}
                                    className={cn(
                                        "px-[10px] py-[6px] rounded-[10px] flex items-center gap-[6px] border-0 transition-colors duration-200",
                                        isDark
                                            ? 'bg-[#cd6d2c] text-[#000000] hover:bg-[#ffffff]'
                                            : 'bg-[#cd6d2c] text-white hover:bg-[#072837]'
                                    )}
                                >
                                    <div
                                        className={cn("w-[16px] h-[16px]")}
                                        style={{
                                            backgroundColor: isDark ? '#000000' : '#ffffff',
                                            WebkitMaskImage: "url('/assets/icons/logdata.svg')",
                                            WebkitMaskRepeat: 'no-repeat',
                                            WebkitMaskSize: 'contain',
                                            WebkitMaskPosition: 'center',
                                            maskImage: "url('/assets/icons/logdata.svg')",
                                            maskRepeat: 'no-repeat',
                                            maskSize: 'contain',
                                            maskPosition: 'center'
                                        }}
                                        aria-hidden="true"
                                    />
                                    <span className={cn("text-[16px] font-semibold")}>Logs</span>
                                </button>
                            )}

                        {/* View Button */}
                        <button
                            type="button"
                            onClick={() => navigate(`/submissions/${submission.id}`)}
                            className={cn(
                                "px-[10px] py-[6px] rounded-[10px] flex items-center gap-[6px] transition-colors duration-200",
                                isDark
                                    ? 'bg-[#f5f5f5] text-[#000000] border-solid border-[1px] border-[#292929] hover:bg-[#cd6d2c]'
                                    : 'bg-[#072837] text-white border-0 hover:bg-[#0b3a4f]'
                            )}
                        >
                            <div
                                className={cn("w-[16px] h-[16px]")}
                                style={{
                                    backgroundColor: isDark ? '#000000' : '#ffffff',
                                    WebkitMaskImage: "url('/assets/icons/viewdata.svg')",
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskSize: 'contain',
                                    WebkitMaskPosition: 'center',
                                    maskImage: "url('/assets/icons/viewdata.svg')",
                                    maskRepeat: 'no-repeat',
                                    maskSize: 'contain',
                                    maskPosition: 'center'
                                }}
                                aria-hidden="true"
                            />
                            <span className={cn("text-[16px] font-semibold")}>View</span>
                        </button>
                    </div>
            };
        })
    };

    return (
        <>
            {reassignModalOpen && reassignTarget && (
                <ReassignModal
                    submission={reassignTarget}
                    userEmail={userTeamData?.email}
                    isDark={isDark}
                    onClose={() => { setReassignModalOpen(false); setReassignTarget(null); }}
                    onSuccess={() => fetchSubmissions()}
                />
            )}
            <ExportLogsModal
                show={isExportModalOpen}
                onHide={() => setIsExportModalOpen(false)}
                onExport={(dateRange) => {
                    console.log('Exporting logs with date range:', dateRange);
                    // Handle export logic here
                    setIsExportModalOpen(false);
                }}
            />
            <div className={cn(
                "min-h-screen px-[16px] sm:px-[24px] py-[20px] sm:py-[30px] flex flex-col gap-[20px] sm:gap-[30px] transition-colors duration-200",
                isDark ? 'bg-[#000000]' : 'bg-[#F5F5F5]'
            )}>
                {/* Header Section */}
                <div className='flex flex-col sm:flex-row gap-[16px] sm:gap-[30px] items-start sm:items-center sm:justify-between'>
                    <div className={cn("flex-1 flex flex-col gap-[6px]")}>
                        <h1
                            className={cn(
                                "font-semibold text-[22px] sm:text-[24px] leading-tight m-0 transition-colors duration-200",
                                isDark ? 'text-white' : 'text-[#072837]'
                            )}
                        >
                            Submissions
                        </h1>
                        <p
                            className={cn(
                                "font-normal text-[14px] sm:text-[16px] leading-normal m-0 transition-colors duration-200",
                                isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                            )}
                        >
                            View and manage your submissions
                        </p>
                    </div>
                    <div className={cn("flex items-center gap-[12px]")}>
                        <button
                            onClick={handleExportFilteredSubmissions}
                            className={cn(
                                "px-[16px] py-[12px] rounded-[12px] flex items-center gap-[6px] whitespace-nowrap font-semibold text-[16px] transition-colors duration-200 border-0",
                                isDark
                                    ? 'bg-white text-[#000000] hover:!bg-[#cd6d2c]'
                                    : 'bg-[#072837] text-white hover:!bg-[#cd6d2c]'
                            )}
                        >
                            <div
                                className={cn("w-[16px] h-[16px]")}
                                style={{
                                    backgroundColor: isDark ? '#000000' : '#ffffff',
                                    WebkitMaskImage: "url('/assets/icons/export.svg')",
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskSize: 'contain',
                                    WebkitMaskPosition: 'center',
                                    maskImage: "url('/assets/icons/export.svg')",
                                    maskRepeat: 'no-repeat',
                                    maskSize: 'contain',
                                    maskPosition: 'center'
                                }}
                                aria-hidden="true"
                            />
                            Export Filtered Submissions
                        </button>
                        {(userTeamData?.user_role_id?.includes('10') || userTeamData?.user_role_id?.includes('11')) && (
                            <button
                                onClick={() => setIsExportModalOpen(true)}
                                className={cn(
                                    "px-[16px] py-[12px] rounded-[12px] flex items-center gap-[6px] whitespace-nowrap font-semibold text-[16px] transition-colors duration-200 border-0",
                                    isDark
                                        ? 'bg-[#cd6d2c] text-[#000000] hover:bg-[#ffffff]'
                                        : 'bg-[#cd6d2c] text-white hover:bg-[#072837]'
                                )}
                            >
                                <div
                                    className={cn("w-[16px] h-[16px]")}
                                    style={{
                                        backgroundColor: isDark ? '#000000' : '#ffffff',
                                        WebkitMaskImage: "url('/assets/icons/export.svg')",
                                        WebkitMaskRepeat: 'no-repeat',
                                        WebkitMaskSize: 'contain',
                                        WebkitMaskPosition: 'center',
                                        maskImage: "url('/assets/icons/export.svg')",
                                        maskRepeat: 'no-repeat',
                                        maskSize: 'contain',
                                        maskPosition: 'center'
                                    }}
                                    aria-hidden="true"
                                />
                                Export Submissions Log
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters Section with integrated Toolbar */}
                <Filters
                    items={datae}
                    activeStatus={statusFilter}
                    onStatusChange={handleStatusFilterChange}
                    viewMode={viewMode}
                    isDark={isDark}
                >
                    <Toolbar
                        searchValue={searchQuery}
                        onSearchChange={(query) => {
                            setSearchQuery(query);
                            setPage(1);
                            if (query.length > 0) { shouldFocusSearch.current = true; }
                        }}
                        planSearchValue={planSearchQuery}
                        onPlanSearchChange={(val) => {
                            setPlanSearchQuery(val);
                            setPage(1);
                        }}
                        dateRange={dateRange}
                        onDateRangeChange={handleDateRangeChange}
                        searchRef={searchInputRef}
                        isDark={isDark}
                        groupingCategoryFilter={
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        className={cn("border-solid border-[1px] flex items-center justify-center gap-[6px] h-[40px] px-[16px] py-[6px] rounded-[12px] text-[16px] font-normal cursor-pointer transition-colors duration-200 whitespace-nowrap")}
                                        style={{
                                            backgroundColor: isDark ? '#161515' : '#ffffff',
                                            borderColor: isDark ? '#292929' : '#d9d9d9',
                                            color: isDark ? '#f2f2f2' : '#072837',
                                            filter: 'drop-shadow(0px 0px 50px rgba(0, 0, 0, 0.05))'
                                        }}
                                        disabled={isLoadingConfigs}
                                    >
                                        <span>
                                            {isLoadingConfigs
                                                ? 'Loading...'
                                                : selectedGroupingCategory === 'all'
                                                    ? 'Grouping Category- All'
                                                    : `Grouping Category- ${groupedCategories.find(cat => cat.id === selectedGroupingCategory || cat.id === parseInt(selectedGroupingCategory))?.name || 'All'}`
                                            }
                                        </span>
                                        <ChevronDown
                                            className={cn("w-[14px] h-[14px] transition-colors duration-200")}
                                            style={{ color: isDark ? '#f2f2f2' : '#072837' }}
                                        />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className={cn(
                                    isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]',
                                    "border border-solid border-[1px] rounded-[12px] p-0 overflow-hidden max-h-[300px] overflow-y-auto w-[var(--radix-dropdown-menu-trigger-width)]"
                                )}
                                >
                                    <DropdownMenuItem
                                        className={cn(isDark
                                            ? selectedGroupingCategory === 'all'
                                                ? 'bg-[#cd6d2c] text-[#161515]'
                                                : 'bg-[#161515] text-[#f2f2f2] hover:bg-[#222222] hover:text-[#cd6d2c]'
                                            : selectedGroupingCategory === 'all'
                                                ? 'bg-[#072837] text-white'
                                                : 'bg-white text-[#072837] hover:!bg-[#f5f5f5]', "text-[13px] font-medium px-[16px] py-[10px] transition-colors duration-200")}
                                        onClick={() => handleGroupingCategoryChange('all')}
                                    >
                                        All Categories
                                    </DropdownMenuItem>
                                    {groupedCategories.map((category) => (
                                        <DropdownMenuItem
                                            key={category.id}
                                            className={cn(isDark
                                                ? (selectedGroupingCategory === category.id || selectedGroupingCategory === String(category.id))
                                                    ? 'bg-[#cd6d2c] text-[#161515]'
                                                    : 'bg-[#161515] text-[#f2f2f2] hover:bg-[#222222] hover:text-[#cd6d2c]'
                                                : (selectedGroupingCategory === category.id || selectedGroupingCategory === String(category.id))
                                                    ? 'bg-[#072837] text-white hover:!bg-[#072837]'
                                                    : 'bg-white text-[#072837] hover:!bg-[#f5f5f5]', "text-[13px] font-medium px-[16px] py-[10px] transition-colors duration-200")}
                                            onClick={() => handleGroupingCategoryChange(category.id)}
                                        >
                                            {category.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        }
                    />
                </Filters>

                {/* Table Section */}
                <SubmissionsTable
                    data={tableData.data}
                    tableData={tableData}
                    title={viewMode === 'my' ? 'My Submissions' : 'All Submissions'}
                    loading={loadingApi}
                    statusFilter={statusFilter}
                    isDark={isDark}
                />
                {/* Pagination Controls */}
                <DataTablePagination
                    page={page}
                    totalPages={totalPages}
                    setPage={setPage}
                    limit={limit}
                    setLimit={setLimit}
                    isDark={isDark}
                />
            </div>
        </>
    );
};

export default Submission;
